import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useI18n } from '../../context/I18nContext';
import './TradelineStrategy.css';
import {
  Shield, Lock, TrendingUp, CheckCircle, AlertTriangle, Info, Target,
  Lightbulb, DollarSign, Calendar, Clock, Percent, Building2, Calculator,
  ChevronRight, AlertCircle, Layers, CreditCard, BarChart3, Zap, Star,
  ArrowRight, RefreshCw, FileText, Check, X
} from 'lucide-react';

// ===================== HELPER FUNCTIONS =====================
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(amount || 0);
};

const formatPercent = (value, decimals = 2) => {
  return `${parseFloat(value || 0).toFixed(decimals)}%`;
};

const formatMonthYear = (date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', year: 'numeric' }).format(date);
};

const addMonths = (date, months) => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

// ===================== INSTITUTIONS DATA =====================
const INSTITUTIONS = [
  { name: "Navy Federal Credit Union", rateRange: "2%-4% above savings rate", gradualRelease: true, notes: "Popular for military members." },
  { name: "PenFed Credit Union", rateRange: "3%-4% above CD/savings rate", gradualRelease: true, notes: "Nationwide membership. Amount must be seasoned >30 days." },
  { name: "Alliant Credit Union", rateRange: "3%-5%", gradualRelease: true, notes: "Online, flexible terms." },
  { name: "Digital Federal Credit Union (DCU)", rateRange: "4%-7%", gradualRelease: true, notes: "Membership required." },
  { name: "Golden 1 Credit Union", rateRange: "2%-5%", gradualRelease: true, notes: "CA-based, strong pledge loan product." },
];

// ===================== PRESETS =====================
const PRESETS = {
  balanced: { paydownPercent: 90, term: 36, apr: 4.0, minPayment: 25, labelKey: 'presetBalanced' },
  lowerInterest: { paydownPercent: 95, term: 24, apr: 3.5, minPayment: 50, labelKey: 'presetLowerInterest' },
  longerReporting: { paydownPercent: 85, term: 48, apr: 4.5, minPayment: 25, labelKey: 'presetLongerReporting' },
};

// ===================== COMPONENT =====================
const TradelineStrategy = () => {
  const { t } = useI18n();
  const TS = 'creditBuilder.tradelineStrategy';
  // Active section for sticky nav
  const [activeSection, setActiveSection] = useState('basics');
  
  // Simulator state
  const [loanAmount, setLoanAmount] = useState(5900);
  const [loanTerm, setLoanTerm] = useState(36);
  const [paydownPercent, setPaydownPercent] = useState(90);
  const [apr, setApr] = useState(4.0);
  const [minPaymentOption, setMinPaymentOption] = useState(25);
  const [startDate, setStartDate] = useState(() => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  });
  const [useCustomStretch, setUseCustomStretch] = useState(false);
  const [customStretchMonths, setCustomStretchMonths] = useState(12);
  const [activePreset, setActivePreset] = useState('balanced');

  // Scroll spy for sticky nav
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['basics', 'release', 'rates', 'simulator', 'stacking', 'institutions', 'action'];
      for (const id of sections) {
        const el = document.getElementById(id);
        if (el) {
          const rect = el.getBoundingClientRect();
          if (rect.top <= 180 && rect.bottom >= 180) {
            setActiveSection(id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Apply preset
  const applyPreset = (presetKey) => {
    const preset = PRESETS[presetKey];
    if (preset) {
      setPaydownPercent(preset.paydownPercent);
      setLoanTerm(preset.term);
      setApr(preset.apr);
      setMinPaymentOption(preset.minPayment);
      setActivePreset(presetKey);
    }
  };

  // Simulator calculations
  const simulatorResults = useMemo(() => {
    const amount = parseFloat(loanAmount) || 0;
    const term = parseInt(loanTerm) || 36;
    const paydown = parseFloat(paydownPercent) || 90;
    const rate = parseFloat(apr) || 4.0;
    const minPay = parseFloat(minPaymentOption) || 0;
    const start = new Date(startDate);
    
    // Early paydown and remaining
    const earlyPaydownAmount = amount * (paydown / 100);
    const remainingBalance = amount - earlyPaydownAmount;
    const remainingRatio = (remainingBalance / amount) * 100;
    
    // Target stretch months
    const targetStretchMonths = useCustomStretch ? customStretchMonths : term;
    
    // Monthly rate
    const monthlyRate = rate / 100 / 12;
    
    // Calculate ideal monthly payment for stretch
    let idealMonthlyPayment = remainingBalance / targetStretchMonths;
    
    // Add simple interest portion
    const monthlyInterest = remainingBalance * monthlyRate;
    idealMonthlyPayment += monthlyInterest;
    
    // Apply minimum payment floor
    let actualMonthlyPayment = Math.max(idealMonthlyPayment, minPay);
    let paymentFloorApplied = actualMonthlyPayment > idealMonthlyPayment;
    
    // Calculate actual payoff months
    let balance = remainingBalance;
    let months = 0;
    let totalInterest = 0;
    
    while (balance > 0.01 && months < 120) {
      const interestThisMonth = balance * monthlyRate;
      totalInterest += interestThisMonth;
      const principalPayment = Math.min(actualMonthlyPayment - interestThisMonth, balance);
      balance -= principalPayment;
      months++;
      if (actualMonthlyPayment >= balance + (balance * monthlyRate)) {
        totalInterest += balance * monthlyRate;
        months++;
        break;
      }
    }
    
    const actualPayoffMonths = months;
    const payoffDate = addMonths(start, actualPayoffMonths);
    const firstReportingMonth = addMonths(start, 1);
    
    // Determine balance ratio status
    let balanceRatioStatus = 'acceptable';
    if (remainingRatio <= 10) {
      balanceRatioStatus = 'excellent';
    } else if (remainingRatio <= 15) {
      balanceRatioStatus = 'good';
    } else if (remainingRatio > 20) {
      balanceRatioStatus = 'warning';
    }
    
    // Strategy interpretation
    let strategyKey = 'balanced';
    let strategyColor = 'green';
    if (paydown >= 95) {
      strategyKey = 'aggressive';
      strategyColor = 'blue';
    } else if (paydown < 85) {
      strategyKey = 'extended';
      strategyColor = 'amber';
    }
    
    // Warnings
    const warnings = [];
    if (paymentFloorApplied) {
      warnings.push({ type: 'floor', min: minPay });
    }
    if (actualPayoffMonths < 6) {
      warnings.push({ type: 'short' });
    }
    if (remainingRatio > 20) {
      warnings.push({ type: 'ratio' });
    }
    
    return {
      totalLoan: amount,
      earlyPaydownAmount,
      remainingBalance,
      remainingRatio,
      balanceRatioStatus,
      idealMonthlyPayment,
      actualMonthlyPayment,
      paymentFloorApplied,
      targetStretchMonths,
      actualPayoffMonths,
      totalInterest,
      payoffDate,
      firstReportingMonth,
      strategyKey,
      strategyColor,
      warnings,
    };
  }, [loanAmount, loanTerm, paydownPercent, apr, minPaymentOption, startDate, useCustomStretch, customStretchMonths]);

  const ratioLabelKey = {
    excellent: 'ratioExcellent',
    good: 'ratioGood',
    acceptable: 'ratioAcceptable',
    warning: 'ratioHigher',
  };

  const strategyLabelKey = {
    balanced: 'strategyBalanced',
    aggressive: 'strategyAggressive',
    extended: 'strategyExtended',
  };

  const warningMessage = (warning) => {
    if (warning.type === 'floor') {
      return t(`${TS}.warnMinPayment`, { min: warning.min }, `Minimum payment ($${warning.min}) overrides target. Loan may close faster than intended.`);
    }
    if (warning.type === 'short') {
      return t(`${TS}.warnShortReporting`);
    }
    return t(`${TS}.warnHigherRatio`);
  };

  const interpretationText = paydownPercent >= 90
    ? t(`${TS}.interpret9010`)
    : paydownPercent >= 85
    ? t(`${TS}.interpret85`)
    : t(`${TS}.interpretBalanced`);

  // Scroll to section
  const scrollToSection = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="ts-page" data-testid="tradeline-strategy">
      {/* ============ HERO ============ */}
      <section className="ts-hero">
        <div className="ts-hero-content">
          <div className="ts-hero-left">
            <span className="ts-eyebrow">
              <span className="ts-eyebrow-dot"></span>
              {t(`${TS}.eyebrow`)}
            </span>
            <h1 className="ts-hero-title">{t(`${TS}.title`)}</h1>
            <p className="ts-hero-desc">
              {t(`${TS}.heroDesc`)}
            </p>
            <div className="ts-hero-badges">
              <span className="ts-badge"><Shield size={14} /> {t(`${TS}.badgeLowRisk`)}</span>
              <span className="ts-badge"><Target size={14} /> {t(`${TS}.badge9010`)}</span>
              <span className="ts-badge"><Layers size={14} /> {t(`${TS}.badgeStacking`)}</span>
            </div>
          </div>
          <div className="ts-hero-right">
            <div className="ts-stat-card">
              <Percent size={20} className="ts-stat-icon" />
              <div className="ts-stat-value">2%-10%</div>
              <div className="ts-stat-label">{t(`${TS}.statRateLabel`)}</div>
            </div>
            <div className="ts-stat-card">
              <Target size={20} className="ts-stat-icon" />
              <div className="ts-stat-value">90 / 10</div>
              <div className="ts-stat-label">{t(`${TS}.stat9010Label`)}</div>
            </div>
            <div className="ts-stat-card ts-stat-highlight">
              <AlertTriangle size={20} className="ts-stat-icon" />
              <div className="ts-stat-value">2-3</div>
              <div className="ts-stat-label">{t(`${TS}.statLoanCautionLabel`)}</div>
            </div>
          </div>
        </div>
      </section>

      {/* ============ STICKY NAV ============ */}
      <nav className="ts-sticky-nav">
        <div className="ts-sticky-nav-inner">
          <button className={`ts-nav-btn ${activeSection === 'basics' ? 'active' : ''}`} onClick={() => scrollToSection('basics')}>
            <FileText size={14} /> {t(`${TS}.navBasics`)}
          </button>
          <button className={`ts-nav-btn ${activeSection === 'release' ? 'active' : ''}`} onClick={() => scrollToSection('release')}>
            <Lock size={14} /> {t(`${TS}.navRelease`)}
          </button>
          <button className={`ts-nav-btn ${activeSection === 'rates' ? 'active' : ''}`} onClick={() => scrollToSection('rates')}>
            <DollarSign size={14} /> {t(`${TS}.navRates`)}
          </button>
          <button className={`ts-nav-btn ${activeSection === 'simulator' ? 'active' : ''}`} onClick={() => scrollToSection('simulator')}>
            <Calculator size={14} /> {t(`${TS}.navSimulator`)}
          </button>
          <button className={`ts-nav-btn ${activeSection === 'stacking' ? 'active' : ''}`} onClick={() => scrollToSection('stacking')}>
            <Layers size={14} /> {t(`${TS}.navStacking`)}
          </button>
          <button className={`ts-nav-btn ${activeSection === 'institutions' ? 'active' : ''}`} onClick={() => scrollToSection('institutions')}>
            <Building2 size={14} /> {t(`${TS}.navInstitutions`)}
          </button>
          <button className={`ts-nav-btn ${activeSection === 'action' ? 'active' : ''}`} onClick={() => scrollToSection('action')}>
            <Zap size={14} /> {t(`${TS}.navAction`)}
          </button>
        </div>
      </nav>

      {/* ============ PLEDGE LOAN BASICS ============ */}
      <section className="ts-section" id="basics">
        <div className="ts-section-header">
          <h2 className="ts-section-title">{t(`${TS}.basicsTitle`)}</h2>
          <p className="ts-section-subtitle">{t(`${TS}.basicsSubtitle`)}</p>
        </div>

        <div className="ts-core-advantage">
          <Lightbulb size={20} />
          <div>
            <strong>{t(`${TS}.coreAdvantage`)}</strong> {t(`${TS}.coreAdvantageBody`)}
          </div>
        </div>

        <div className="ts-steps-grid">
          <div className="ts-step-card">
            <div className="ts-step-num">1</div>
            <div className="ts-step-content">
              <h4>{t(`${TS}.step1Title`)}</h4>
              <p>{t(`${TS}.step1Body`)}</p>
            </div>
          </div>
          <div className="ts-step-card">
            <div className="ts-step-num">2</div>
            <div className="ts-step-content">
              <h4>{t(`${TS}.step2Title`)}</h4>
              <p>{t(`${TS}.step2Body`)}</p>
            </div>
          </div>
          <div className="ts-step-card">
            <div className="ts-step-num">3</div>
            <div className="ts-step-content">
              <h4>{t(`${TS}.step3Title`)}</h4>
              <p>{t(`${TS}.step3Body`)}</p>
            </div>
          </div>
          <div className="ts-step-card">
            <div className="ts-step-num">4</div>
            <div className="ts-step-content">
              <h4>{t(`${TS}.step4Title`)}</h4>
              <p>{t(`${TS}.step4Body`)}</p>
            </div>
          </div>
        </div>

        <div className="ts-benefits-row">
          <div className="ts-benefit-card ts-benefit-positive">
            <CheckCircle size={18} />
            <div>
              <strong>{t(`${TS}.benefitOnTimeTitle`)}</strong>
              <p>{t(`${TS}.benefitOnTimeBody`)}</p>
            </div>
          </div>
          <div className="ts-benefit-card ts-benefit-positive">
            <DollarSign size={18} />
            <div>
              <strong>{t(`${TS}.benefitLowerRatesTitle`)}</strong>
              <p>{t(`${TS}.benefitLowerRatesBody`)}</p>
            </div>
          </div>
          <div className="ts-benefit-card ts-benefit-caution">
            <AlertTriangle size={18} />
            <div>
              <strong>{t(`${TS}.benefitDefaultTitle`)}</strong>
              <p>{t(`${TS}.benefitDefaultBody`)}</p>
            </div>
          </div>
        </div>

        <div className="ts-example-box">
          <div className="ts-example-header">
            <FileText size={16} />
            <span>{t(`${TS}.exampleTitle`)}</span>
          </div>
          <p>{t(`${TS}.exampleBody`)}</p>
        </div>
      </section>

      {/* ============ GRADUAL RELEASE ============ */}
      <section className="ts-section ts-section-alt" id="release">
        <div className="ts-section-header">
          <h2 className="ts-section-title">{t(`${TS}.releaseTitle`)}</h2>
          <p className="ts-section-subtitle">{t(`${TS}.releaseSubtitleFull`, null, t(`${TS}.releaseSubtitle`))}</p>
        </div>

        <div className="ts-compare-cards">
          <div className="ts-compare-card">
            <div className="ts-compare-header ts-compare-traditional">
              <Lock size={18} />
              <span>{t(`${TS}.traditionalLabel`)}</span>
            </div>
            <ul className="ts-compare-list">
              <li><X size={14} className="ts-x" /> {t(`${TS}.tradCollLocked`)}</li>
              <li><CheckCircle size={14} className="ts-check" /> {t(`${TS}.tradLowRisk`)}</li>
              <li><X size={14} className="ts-x" /> {t(`${TS}.tradNoAccess`)}</li>
              <li><CheckCircle size={14} className="ts-check" /> {t(`${TS}.tradSimplest`)}</li>
            </ul>
          </div>
          <div className="ts-compare-card ts-compare-preferred">
            <div className="ts-compare-header ts-compare-gradual">
              <RefreshCw size={18} />
              <span>{t(`${TS}.gradualLabel`)}</span>
              <span className="ts-preferred-badge">{t(`${TS}.moreFlexible`)}</span>
            </div>
            <ul className="ts-compare-list">
              <li><CheckCircle size={14} className="ts-check" /> {t(`${TS}.gradualCollReleased`)}</li>
              <li><CheckCircle size={14} className="ts-check" /> {t(`${TS}.gradualModerateRisk`)}</li>
              <li><CheckCircle size={14} className="ts-check" /> {t(`${TS}.gradualAccessEarly`)}</li>
              <li><CheckCircle size={14} className="ts-check" /> {t(`${TS}.gradualCashFlow`)}</li>
            </ul>
          </div>
        </div>

        <div className="ts-compare-table-wrap">
          <table className="ts-compare-table">
            <thead>
              <tr>
                <th>{t(`${TS}.tableFeature`)}</th>
                <th>{t(`${TS}.tableTraditional`)}</th>
                <th>{t(`${TS}.tableGradual`)}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{t(`${TS}.accessCollateral`)}</td>
                <td>{t(`${TS}.lockedUntilPayoff`)}</td>
                <td className="ts-highlight">{t(`${TS}.releasedGradually`)}</td>
              </tr>
              <tr>
                <td>{t(`${TS}.lenderRisk`)}</td>
                <td>{t(`${TS}.veryLow`)}</td>
                <td>{t(`${TS}.moderate`)}</td>
              </tr>
              <tr>
                <td>{t(`${TS}.borrowerFlexibility`)}</td>
                <td>{t(`${TS}.noneUntilPayoff`)}</td>
                <td className="ts-highlight">{t(`${TS}.highAccessEarly`)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="ts-warning-banner">
          <AlertTriangle size={18} />
          <div>
            <strong>{t(`${TS}.verifyReleaseTitle`)}</strong>
            <p>{t(`${TS}.verifyReleaseBody`)}</p>
          </div>
        </div>
      </section>

      {/* ============ RATES ============ */}
      <section className="ts-section" id="rates">
        <div className="ts-section-header">
          <h2 className="ts-section-title">{t(`${TS}.ratesTitle`)}</h2>
          <p className="ts-section-subtitle">{t(`${TS}.ratesSubtitle`)}</p>
        </div>

        <div className="ts-rates-grid">
          <div className="ts-rate-card">
            <div className="ts-rate-value">2%-10%</div>
            <div className="ts-rate-label">{t(`${TS}.typicalRange`)}</div>
          </div>
          <div className="ts-rate-card">
            <div className="ts-rate-value">$147.21</div>
            <div className="ts-rate-label">{t(`${TS}.monthlyAt4`)}</div>
          </div>
          <div className="ts-rate-card">
            <div className="ts-rate-value">$154.94</div>
            <div className="ts-rate-label">{t(`${TS}.monthlyAt7`)}</div>
          </div>
          <div className="ts-rate-card ts-rate-impact">
            <div className="ts-rate-value">~$279</div>
            <div className="ts-rate-label">{t(`${TS}.extraInterest3`)}</div>
          </div>
        </div>

        <div className="ts-factors-row">
          <div className="ts-factor-card">
            <h4>{t(`${TS}.factorsAffectingRate`)}</h4>
            <ul>
              <li>{t(`${TS}.factorSavingsVsCd`)}</li>
              <li>{t(`${TS}.factorTermLength`)}</li>
              <li>{t(`${TS}.factorLoanAmount`)}</li>
              <li>{t(`${TS}.factorBorrowerProfile`)}</li>
              <li>{t(`${TS}.factorLenderPolicy`)}</li>
            </ul>
          </div>
          <div className="ts-factor-card ts-factor-highlight">
            <h4>{t(`${TS}.whyRateMatters`)}</h4>
            <p>{t(`${TS}.whyRateMattersBody`)}</p>
          </div>
        </div>
      </section>

      {/* ============ 90/10 SIMULATOR ============ */}
      <section className="ts-section ts-section-alt" id="simulator">
        <div className="ts-section-header">
          <h2 className="ts-section-title">{t(`${TS}.simulatorTitle`)}</h2>
          <p className="ts-section-subtitle">{t(`${TS}.simulatorSubtitleFull`, null, t(`${TS}.simulatorSubtitle`))}</p>
        </div>

        <div className="ts-simulator-best-use">
          <Lightbulb size={18} />
          <div>
            <strong>{t(`${TS}.bestUseCase`)}</strong> {t(`${TS}.bestUseCaseBodyFull`, null, t(`${TS}.bestUseCaseBody`))}
          </div>
        </div>

        <div className="ts-simulator-panel">
          <div className="ts-presets">
            <span className="ts-presets-label">{t(`${TS}.presets`)}</span>
            {Object.entries(PRESETS).map(([key, preset]) => (
              <button
                key={key}
                className={`ts-preset-chip ${activePreset === key ? 'active' : ''}`}
                onClick={() => applyPreset(key)}
              >
                {t(`${TS}.${preset.labelKey}`)}
              </button>
            ))}
            <button
              className={`ts-preset-chip ${activePreset === 'custom' ? 'active' : ''}`}
              onClick={() => setActivePreset('custom')}
            >
              {t(`${TS}.presetCustom`)}
            </button>
          </div>

          <div className="ts-simulator-grid">
            {/* Controls */}
            <div className="ts-controls-column">
              <div className="ts-control-card">
                <label className="ts-control-label">
                  <DollarSign size={14} /> {t(`${TS}.loanAmount`)}
                </label>
                <input
                  type="range"
                  min="500"
                  max="25000"
                  step="100"
                  value={loanAmount}
                  onChange={(e) => { setLoanAmount(e.target.value); setActivePreset('custom'); }}
                  className="ts-slider"
                />
                <div className="ts-control-value">{formatCurrency(loanAmount)}</div>
              </div>

              <div className="ts-control-card">
                <label className="ts-control-label">
                  <Calendar size={14} /> {t(`${TS}.originalLoanTerm`)}
                </label>
                <input
                  type="range"
                  min="12"
                  max="60"
                  step="6"
                  value={loanTerm}
                  onChange={(e) => { setLoanTerm(e.target.value); setActivePreset('custom'); }}
                  className="ts-slider"
                />
                <div className="ts-control-value">{t(`${TS}.monthsCount`, { count: loanTerm }, `${loanTerm} months`)}</div>
              </div>

              <div className="ts-control-card ts-control-highlight">
                <label className="ts-control-label">
                  <Target size={14} /> {t(`${TS}.earlyPaydownPct`)}
                </label>
                <input
                  type="range"
                  min="50"
                  max="99"
                  step="1"
                  value={paydownPercent}
                  onChange={(e) => { setPaydownPercent(e.target.value); setActivePreset('custom'); }}
                  className="ts-slider"
                />
                <div className="ts-control-value">{paydownPercent}%</div>
              </div>

              <div className="ts-control-card">
                <label className="ts-control-label">
                  <Percent size={14} /> {t(`${TS}.aprRate`)}
                </label>
                <input
                  type="range"
                  min="1"
                  max="12"
                  step="0.25"
                  value={apr}
                  onChange={(e) => { setApr(e.target.value); setActivePreset('custom'); }}
                  className="ts-slider"
                />
                <div className="ts-control-value">{formatPercent(apr)}</div>
              </div>

              <div className={`ts-control-card ${simulatorResults.paymentFloorApplied ? 'ts-control-warn' : ''}`}>
                <label className="ts-control-label">
                  <AlertCircle size={14} /> {t(`${TS}.minMonthlyPayment`)}
                </label>
                <select
                  value={minPaymentOption}
                  onChange={(e) => { setMinPaymentOption(e.target.value); setActivePreset('custom'); }}
                  className="ts-select"
                >
                  <option value="0">{t(`${TS}.minPaymentNoFloor`)}</option>
                  <option value="25">$25</option>
                  <option value="50">$50</option>
                  <option value="75">$75</option>
                  <option value="100">$100</option>
                </select>
                {simulatorResults.paymentFloorApplied && (
                  <div className="ts-control-help ts-control-help-warn">
                    <AlertTriangle size={12} /> {t(`${TS}.lenderFloorShorten`)}
                  </div>
                )}
              </div>

              <div className="ts-control-card">
                <label className="ts-control-label">
                  <Calendar size={14} /> {t(`${TS}.loanStartDate`)}
                </label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="ts-date-input"
                />
              </div>

              <div className="ts-control-card ts-stretch-control">
                <label className="ts-control-label">
                  <Clock size={14} /> {t(`${TS}.remainingMonthsToMaturity`)}
                </label>
                <div className="ts-stretch-options">
                  <label className="ts-radio-label">
                    <input
                      type="radio"
                      checked={!useCustomStretch}
                      onChange={() => setUseCustomStretch(false)}
                    />
                    {t(`${TS}.autoSyncLoanTerm`)}
                  </label>
                  <label className="ts-radio-label">
                    <input
                      type="radio"
                      checked={useCustomStretch}
                      onChange={() => setUseCustomStretch(true)}
                    />
                    {t(`${TS}.useCustomStretch`)}
                  </label>
                </div>
                {useCustomStretch && (
                  <>
                    <input
                      type="range"
                      min="3"
                      max="60"
                      step="1"
                      value={customStretchMonths}
                      onChange={(e) => setCustomStretchMonths(e.target.value)}
                      className="ts-slider"
                    />
                    <div className="ts-control-value">{t(`${TS}.monthsCount`, { count: customStretchMonths }, `${customStretchMonths} months`)}</div>
                  </>
                )}
              </div>
            </div>

            {/* Results */}
            <div className="ts-results-column">
              <div className="ts-results-header">
                <BarChart3 size={18} />
                <span>{t(`${TS}.simulatorResults`)}</span>
              </div>

              <div className="ts-results-grid">
                <div className="ts-result-card">
                  <span className="ts-result-label">{t(`${TS}.totalLoan`)}</span>
                  <span className="ts-result-value">{formatCurrency(simulatorResults.totalLoan)}</span>
                </div>
                <div className="ts-result-card ts-result-highlight">
                  <span className="ts-result-label">{t(`${TS}.payEarlyPercent`, { percent: paydownPercent })}</span>
                  <span className="ts-result-value">{formatCurrency(simulatorResults.earlyPaydownAmount)}</span>
                </div>
                <div className="ts-result-card">
                  <span className="ts-result-label">{t(`${TS}.remainingPercent`, { percent: 100 - paydownPercent })}</span>
                  <span className="ts-result-value">{formatCurrency(simulatorResults.remainingBalance)}</span>
                </div>
                <div className="ts-result-card">
                  <span className="ts-result-label">{t(`${TS}.remainingBalanceRatio`)}</span>
                  <span className="ts-result-value">
                    {formatPercent(simulatorResults.remainingRatio)}
                    <span className={`ts-ratio-badge ts-ratio-${simulatorResults.balanceRatioStatus}`}>
                      {t(`${TS}.${ratioLabelKey[simulatorResults.balanceRatioStatus]}`)}
                    </span>
                  </span>
                </div>
                <div className="ts-result-card">
                  <span className="ts-result-label">{t(`${TS}.estimatedMonthlyStretch`)}</span>
                  <span className="ts-result-value">{formatCurrency(simulatorResults.actualMonthlyPayment)}</span>
                </div>
                <div className="ts-result-card">
                  <span className="ts-result-label">{t(`${TS}.targetReportingWindow`)}</span>
                  <span className="ts-result-value">{t(`${TS}.monthsCount`, { count: simulatorResults.actualPayoffMonths })}</span>
                </div>
                <div className="ts-result-card">
                  <span className="ts-result-label">{t(`${TS}.estimatedCarryInterest`)}</span>
                  <span className="ts-result-value">{formatCurrency(simulatorResults.totalInterest)}</span>
                </div>
                <div className="ts-result-card">
                  <span className="ts-result-label">{t(`${TS}.estimatedPayoffDate`)}</span>
                  <span className="ts-result-value">{formatMonthYear(simulatorResults.payoffDate)}</span>
                </div>
                <div className="ts-result-card">
                  <span className="ts-result-label">{t(`${TS}.paymentFloorApplied`)}</span>
                  <span className="ts-result-value">
                    {formatCurrency(simulatorResults.actualMonthlyPayment)}
                    <span className="ts-floor-note">
                      {simulatorResults.paymentFloorApplied
                        ? t(`${TS}.lenderMinApplied`)
                        : t(`${TS}.targetPaymentUsed`)}
                    </span>
                  </span>
                </div>
                <div className="ts-result-card">
                  <span className="ts-result-label">{t(`${TS}.estFirstReportingMonth`)}</span>
                  <span className="ts-result-value">{formatMonthYear(simulatorResults.firstReportingMonth)}</span>
                </div>
              </div>

              <div className="ts-interpretation">
                <div className="ts-interpretation-header">
                  <span className={`ts-strategy-pill ts-strategy-${simulatorResults.strategyColor}`}>
                    {t(`${TS}.${strategyLabelKey[simulatorResults.strategyKey]}`)}
                  </span>
                  <span className="ts-strategy-pill ts-strategy-neutral">
                    {t(`${TS}.payoffWindow`, { count: simulatorResults.actualPayoffMonths })}
                  </span>
                </div>
                <p className="ts-interpretation-text">{interpretationText}</p>
              </div>

              {simulatorResults.warnings.length > 0 && (
                <div className="ts-warnings">
                  <div className="ts-warnings-header">
                    <AlertTriangle size={16} />
                    <span>{t(`${TS}.strategyWarnings`)}</span>
                  </div>
                  {simulatorResults.warnings.map((warning, i) => (
                    <div key={i} className="ts-warning-item">
                      <AlertCircle size={14} />
                      <span>{warningMessage(warning)}</span>
                    </div>
                  ))}
                </div>
              )}

              <div className="ts-insights-grid">
                <div className="ts-insight-card">
                  <div className="ts-insight-header">
                    <Lightbulb size={14} /> {t(`${TS}.insightWhyWorks`)}
                  </div>
                  <p>{t(`${TS}.insightWhyWorksBody`)}</p>
                </div>
                <div className="ts-insight-card">
                  <div className="ts-insight-header">
                    <DollarSign size={14} /> {t(`${TS}.insightPaymentTip`)}
                  </div>
                  <p>{t(`${TS}.insightPaymentTipBody`)}</p>
                </div>
                <div className="ts-insight-card">
                  <div className="ts-insight-header">
                    <CheckCircle size={14} /> {t(`${TS}.insightAutopay`)}
                  </div>
                  <p>{t(`${TS}.insightAutopayBody`)}</p>
                </div>
              </div>
            </div>
          </div>

          <div className="ts-case-snapshot">
            <div className="ts-case-header">
              <Star size={16} />
              <span>{t(`${TS}.caseSnapshotTitle`)}</span>
            </div>
            <div className="ts-case-grid">
              <div className="ts-case-stat">
                <span className="ts-case-label">{t(`${TS}.caseOriginalLoan`)}</span>
                <span className="ts-case-value">$10,000</span>
              </div>
              <div className="ts-case-stat">
                <span className="ts-case-label">{t(`${TS}.caseCurrentBalance`)}</span>
                <span className="ts-case-value ts-case-highlight">$140.42</span>
              </div>
              <div className="ts-case-stat">
                <span className="ts-case-label">{t(`${TS}.caseBalanceRatio`)}</span>
                <span className="ts-case-value ts-case-excellent">1.40%</span>
              </div>
              <div className="ts-case-stat">
                <span className="ts-case-label">{t(`${TS}.caseApr`)}</span>
                <span className="ts-case-value">2.25%</span>
              </div>
              <div className="ts-case-stat">
                <span className="ts-case-label">{t(`${TS}.casePaymentsMade`)}</span>
                <span className="ts-case-value">34</span>
              </div>
              <div className="ts-case-stat">
                <span className="ts-case-label">{t(`${TS}.caseMonthsRemaining`)}</span>
                <span className="ts-case-value">2</span>
              </div>
              <div className="ts-case-stat">
                <span className="ts-case-label">{t(`${TS}.caseDailyAccrual`)}</span>
                <span className="ts-case-value">$0.01</span>
              </div>
            </div>
            <div className="ts-case-takeaway">
              <CheckCircle size={16} />
              <div>
                <strong>{t(`${TS}.caseTakeaway`)}</strong> {t(`${TS}.caseTakeawayBody`)}
              </div>
            </div>
          </div>

          <div className="ts-simulator-disclaimer">
            <Info size={14} />
            <span>{t(`${TS}.disclaimer`)}</span>
          </div>
        </div>
      </section>

      {/* ============ STACKING ============ */}
      <section className="ts-section" id="stacking">
        <div className="ts-section-header">
          <h2 className="ts-section-title">{t(`${TS}.stackingTitle`)}</h2>
          <p className="ts-section-subtitle">{t(`${TS}.stackingSubtitleFull`, null, t(`${TS}.stackingSubtitle`))}</p>
        </div>

        <div className="ts-stacking-steps">
          <div className="ts-stacking-step">
            <div className="ts-stacking-num">1</div>
            <div className="ts-stacking-content">
              <h4>{t(`${TS}.stackStep1Title`)}</h4>
              <p>{t(`${TS}.stackStep1Body`)}</p>
            </div>
          </div>
          <div className="ts-stacking-arrow"><ArrowRight size={20} /></div>
          <div className="ts-stacking-step">
            <div className="ts-stacking-num">2</div>
            <div className="ts-stacking-content">
              <h4>{t(`${TS}.stackStep2Title`)}</h4>
              <p>{t(`${TS}.stackStep2Body`)}</p>
            </div>
          </div>
          <div className="ts-stacking-arrow"><ArrowRight size={20} /></div>
          <div className="ts-stacking-step">
            <div className="ts-stacking-num">3</div>
            <div className="ts-stacking-content">
              <h4>{t(`${TS}.stackStep3Title`)}</h4>
              <p>{t(`${TS}.stackStep3Body`)}</p>
            </div>
          </div>
        </div>

        <div className="ts-stacking-table-wrap">
          <table className="ts-stacking-table">
            <thead>
              <tr>
                <th>{t(`${TS}.tableLoan`)}</th>
                <th>{t(`${TS}.tableOriginalAmount`)}</th>
                <th>{t(`${TS}.tableEarlyPaydown`)}</th>
                <th>{t(`${TS}.tableStretchedBalance`)}</th>
                <th>{t(`${TS}.tableWhyItHelps`)}</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td><span className="ts-loan-badge">{t(`${TS}.stackLoan1`)}</span></td>
                <td>$1,000</td>
                <td>$900</td>
                <td>$100</td>
                <td>{t(`${TS}.stackReason1`)}</td>
              </tr>
              <tr>
                <td><span className="ts-loan-badge ts-loan-2">{t(`${TS}.stackLoan2`)}</span></td>
                <td>$2,000</td>
                <td>$1,900</td>
                <td>$100</td>
                <td>{t(`${TS}.stackReason2`)}</td>
              </tr>
              <tr>
                <td><span className="ts-loan-badge ts-loan-3">{t(`${TS}.stackLoan3`)}</span></td>
                <td>$3,000</td>
                <td>$2,900</td>
                <td>$100</td>
                <td>{t(`${TS}.stackReason3`)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <div className="ts-stacking-cards">
          <div className="ts-stacking-card ts-stacking-benefits">
            <h4><CheckCircle size={16} /> {t(`${TS}.stackingBenefits`)}</h4>
            <ul>
              <li>{t(`${TS}.stackingBenefit1`)}</li>
              <li>{t(`${TS}.stackingBenefit2`)}</li>
              <li>{t(`${TS}.stackingBenefit3`)}</li>
              <li>{t(`${TS}.stackingBenefit4`)}</li>
            </ul>
          </div>
          <div className="ts-stacking-card ts-stacking-cautions">
            <h4><AlertTriangle size={16} /> {t(`${TS}.stackingCautions`)}</h4>
            <ul>
              <li>{t(`${TS}.stackingCaution1`)}</li>
              <li>{t(`${TS}.stackingCaution2`)}</li>
              <li>{t(`${TS}.stackingCaution3`)}</li>
              <li>{t(`${TS}.stackingCaution4`)}</li>
            </ul>
          </div>
        </div>
      </section>

      <section className="ts-section ts-section-alt" id="institutions">
        <div className="ts-section-header">
          <h2 className="ts-section-title">{t(`${TS}.institutionsTitleFull`, null, t(`${TS}.institutionsTitle`))}</h2>
          <p className="ts-section-subtitle">{t(`${TS}.institutionsSubtitleFull`, null, t(`${TS}.institutionsSubtitle`))}</p>
        </div>

        <div className="ts-institutions-note">
          <Info size={16} />
          <div>
            <strong>{t(`${TS}.institutionsNoteTitle`)}</strong> {t(`${TS}.institutionsNoteBody`)}
          </div>
        </div>

        <div className="ts-institutions-table-wrap">
          <table className="ts-institutions-table">
            <thead>
              <tr>
                <th>{t(`${TS}.tableInstitution`)}</th>
                <th>{t(`${TS}.tableInterestRateRange`)}</th>
                <th>{t(`${TS}.tableGradualReleaseQ`)}</th>
                <th>{t(`${TS}.tableNotes`)}</th>
              </tr>
            </thead>
            <tbody>
              {INSTITUTIONS.map((inst, i) => (
                <tr key={i}>
                  <td><strong>{inst.name}</strong></td>
                  <td>{inst.rateRange}</td>
                  <td>
                    {inst.gradualRelease ? (
                      <span className="ts-release-yes"><CheckCircle size={14} /> {t(`${TS}.yes`)}</span>
                    ) : (
                      <span className="ts-release-no"><X size={14} /> {t(`${TS}.no`)}</span>
                    )}
                  </td>
                  <td className="ts-inst-notes">{inst.notes}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="ts-verify-banner">
          <AlertCircle size={18} />
          <div>
            <strong>{t(`${TS}.verifyBeforeTitle`)}</strong>
            <p>{t(`${TS}.verifyBeforeBody`)}</p>
          </div>
        </div>
      </section>

      {/* ============ ACTION PLAN ============ */}
      <section className="ts-section" id="action">
        <div className="ts-section-header">
          <h2 className="ts-section-title">{t(`${TS}.actionTitle`, null, 'Quick Action Plan')}</h2>
          <p className="ts-section-subtitle">{t(`${TS}.actionSubtitleFull`, null, t(`${TS}.actionSubtitle`))}</p>
        </div>

        <div className="ts-action-grid">
          {[1, 2, 3, 4, 5, 6].map((num) => (
            <div key={num} className="ts-action-card">
              <div className="ts-action-num">{num}</div>
              <div className="ts-action-content">
                <h4>{t(`${TS}.actionStep${num}Title`)}</h4>
                <p>{t(`${TS}.actionStep${num}Body`)}</p>
              </div>
            </div>
          ))}
        </div>

        <div className="ts-roadmap-cards">
          <div className="ts-roadmap-card ts-roadmap-do">
            <div className="ts-roadmap-header">
              <CheckCircle size={18} />
              <span>{t(`${TS}.underwriterTitle`)}</span>
            </div>
            <ul>
              <li>{t(`${TS}.underwriter1`)}</li>
              <li>{t(`${TS}.underwriter2`)}</li>
              <li>{t(`${TS}.underwriter3`)}</li>
              <li>{t(`${TS}.underwriter4`)}</li>
            </ul>
          </div>
          <div className="ts-roadmap-card ts-roadmap-dont">
            <div className="ts-roadmap-header">
              <X size={18} />
              <span>{t(`${TS}.whatNotToDo`)}</span>
            </div>
            <ul>
              <li>{t(`${TS}.whatNotToDo1`)}</li>
              <li>{t(`${TS}.whatNotToDo2`)}</li>
              <li>{t(`${TS}.whatNotToDo3`)}</li>
              <li>{t(`${TS}.whatNotToDo4`)}</li>
            </ul>
          </div>
        </div>

        <div className="ts-disclaimer-banner">
          <Info size={18} />
          <div>
            <strong>{t(`${TS}.educationalDisclaimerTitle`)}</strong>
            <p>{t(`${TS}.educationalDisclaimerBody`)}</p>
          </div>
        </div>
      </section>

      <div className="ts-footer">
        {t(`${TS}.footer`)}
      </div>
    </div>
  );
};

export default TradelineStrategy;
